/**
 * SSR/CSR 공용 라우트 정의.
 *
 * SSR 대상 공개 페이지는 직접 import (lazy 미사용).
 * CSR 전용 페이지(admin, auth 등)는 lazy import.
 */
import { Suspense } from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import ScrollToTop from '@/components/common/ScrollToTop'
import SEO from '@/components/common/SEO'
import Spinner from '@/components/ui/Spinner'
import { useAuthStore } from '@/stores/authStore'
import { decideFacilityAccess, getActAsTarget } from '@/features/facility-manager/actAs'
import { useAuthHydrated } from '@/hooks/useAuthHydrated'
import { useSsrStatus } from '@/hooks/useSsrStatus'
import { lazyWithRetry } from '@/lib/lazyWithRetry'
import { findNoindexRoute } from '@/lib/seo/privateRoutes'

// =========================================================================
// SSR 대상: 공개 페이지 (직접 import — 서버 렌더링 가능)
// =========================================================================
import HomePage from '@/pages/HomePage'
import FacilitySearchPage from '@/pages/FacilitySearchPage'
import FacilityDetailPage from '@/pages/FacilityDetailPage'
import RegionalFacilityPage from '@/pages/RegionalFacilityPage'
// 커뮤니티 SSR 대상 — lazy 이면 renderToString 이 Suspense 폴백을 렌더해 SEO 색인 불가.
// 정적 import 로 SSR 동기 렌더 보장 (시설/지역 페이지와 동일).
import CommunityHomePage from '@/pages/community/CommunityHomePage'
import BoardPage from '@/pages/community/BoardPage'
import PostDetailPage from '@/pages/community/PostDetailPage'
import CostSimulatorPage from '@/pages/CostSimulatorPage'
import CareCalculatorStartPage from '@/pages/carecalc/CareCalculatorStartPage'
import CareCalculatorProfilePage from '@/pages/carecalc/CareCalculatorProfilePage'
import CareCalculatorStepPage from '@/pages/carecalc/CareCalculatorStepPage'
import CareCalculatorResultPage from '@/pages/carecalc/CareCalculatorResultPage'
import AboutPage from '@/pages/AboutPage'
import ContactPage from '@/pages/ContactPage'
import GuideCareGradePage from '@/pages/GuideCareGradePage'
import GuideFacilityTypePage from '@/pages/GuideFacilityTypePage'
import GuideCostPage from '@/pages/GuideCostPage'
import GuideResourcesPage from '@/pages/GuideResourcesPage'
import SelfAssessmentPage from '@/pages/SelfAssessmentPage'
import SelfAssessmentResultPage from '@/pages/SelfAssessmentResultPage'
// 체크리스트 소개는 사이트맵 URL — lazy 면 SSR 이 Suspense 폴백(스피너)만 렌더해 title·H1 이 빠진다
// (ssr-spec §15.4). 응답/결과 페이지는 개인 화면(noindex)이라 lazy 유지.
import ChecklistIntroPage from '@/pages/ChecklistIntroPage'
import TermsPage from '@/pages/TermsPage'
import ComparePage from '@/pages/ComparePage'
import BlogListPage from '@/pages/blog/BlogListPage'
import BlogCategoryPage from '@/pages/blog/BlogCategoryPage'
import BlogDetailPage from '@/pages/blog/BlogDetailPage'
import BizIntroPage from '@/pages/biz/BizIntroPage'
import NotFoundPage from '@/pages/NotFoundPage'
import { useHost, isBizHost } from '@/hooks/useHost'

// =========================================================================
// CSR 전용: 인증/관리자 페이지 (lazy import — 번들 분할)
// =========================================================================
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'))
const SignupPage = lazyWithRetry(() => import('@/pages/SignupPage'))
const RecommendInputPage = lazyWithRetry(() => import('@/pages/RecommendInputPage'))
const RecommendResultPage = lazyWithRetry(() => import('@/pages/RecommendResultPage'))
const BookmarkPage = lazyWithRetry(() => import('@/pages/BookmarkPage'))
const MyPage = lazyWithRetry(() => import('@/pages/MyPage'))
const MyPageEdit = lazyWithRetry(() => import('@/pages/MyPageEdit'))
const ParentInfoPage = lazyWithRetry(() => import('@/pages/ParentInfoPage'))
const MyConsultationsPage = lazyWithRetry(() => import('@/pages/MyConsultationsPage'))
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminDashboardPage'))
const AdminConsultationsPage = lazyWithRetry(() => import('@/pages/admin/AdminConsultationsPage'))
const AdminFacilityManagersPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilityManagersPage'))
const AdminFacilityManagerDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilityManagerDetailPage'))
const AdminInquiriesPage = lazyWithRetry(() => import('@/pages/admin/AdminInquiriesPage'))
const AdminReviewsPage = lazyWithRetry(() => import('@/pages/admin/AdminReviewsPage'))
const AdminPromotionsPage = lazyWithRetry(() => import('@/pages/admin/AdminPromotionsPage'))
const AdminBlogListPage = lazyWithRetry(() => import('@/pages/admin/AdminBlogListPage'))
const AdminBlogEditorPage = lazyWithRetry(() => import('@/pages/admin/AdminBlogEditorPage'))
const AdminPushSendPage = lazyWithRetry(() => import('@/pages/admin/PushSendPage'))
const AdminPopupListPage = lazyWithRetry(() => import('@/pages/admin/PopupListPage'))
const AdminPopupEditorPage = lazyWithRetry(() => import('@/pages/admin/PopupEditorPage'))
const AdminSubscriptionsPage = lazyWithRetry(() => import('@/pages/admin/AdminSubscriptionsPage'))
const AdminErrorLogsPage = lazyWithRetry(() => import('@/pages/admin/AdminErrorLogsPage'))
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/AdminUsersPage'))
const AdminUserDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminUserDetailPage'))
const AdminFacilityPhotosPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilityPhotosPage'))
const AdminLoginPage = lazyWithRetry(() => import('@/pages/admin/AdminLoginPage'))
const AdminEmailPage = lazyWithRetry(() => import('@/pages/admin/AdminEmailPage'))
const AdminNewsletterListPage = lazyWithRetry(() => import('@/pages/admin/AdminNewsletterListPage'))
const AdminNewsletterEditorPage = lazyWithRetry(() => import('@/pages/admin/AdminNewsletterEditorPage'))
const AdminCouponListPage = lazyWithRetry(() => import('@/pages/admin/AdminCouponListPage'))
const AdminCouponCreatePage = lazyWithRetry(() => import('@/pages/admin/AdminCouponCreatePage'))
const AdminCouponDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminCouponDetailPage'))
const AdminCardNewsPage = lazyWithRetry(() => import('@/pages/admin/AdminCardNewsPage'))
const AdminFacilityDataPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilityDataPage'))
const AdminFacilitySearchPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilitySearchPage'))
const AdminFacilityDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminFacilityDetailPage'))
const AdminBoardsPage = lazyWithRetry(() => import('@/pages/admin/community/AdminBoardsPage'))
const AdminBoardEditPage = lazyWithRetry(() => import('@/pages/admin/community/AdminBoardEditPage'))
const AdminOAuthClientsPage = lazyWithRetry(() => import('@/pages/admin/oauth2/AdminOAuthClientsPage'))
const ConsentPage = lazyWithRetry(() => import('@/pages/oauth2/ConsentPage'))
const BridgePage = lazyWithRetry(() => import('@/pages/oauth2/BridgePage'))
const ShopRedirectPage = lazyWithRetry(() => import('@/pages/ShopRedirectPage'))
const PostWritePage = lazyWithRetry(() => import('@/pages/community/PostWritePage'))
const UnsubscribePage = lazyWithRetry(() => import('@/pages/UnsubscribePage'))
const NotificationSettingsPage = lazyWithRetry(() => import('@/pages/NotificationSettingsPage'))
const CompleteSignupPage = lazyWithRetry(() => import('@/pages/CompleteSignupPage'))
const CompleteProfilePage = lazyWithRetry(() => import('@/pages/CompleteProfilePage'))
const InquiryListPage = lazyWithRetry(() => import('@/pages/InquiryListPage'))
const InquiryNewPage = lazyWithRetry(() => import('@/pages/InquiryNewPage'))
const InquiryDetailPage = lazyWithRetry(() => import('@/pages/InquiryDetailPage'))
const AdminLayout = lazyWithRetry(() => import('@/components/layout/AdminLayout'))
const FacilityLoginPage = lazyWithRetry(() => import('@/pages/facility/FacilityLoginPage'))
const FacilitySignupPage = lazyWithRetry(() => import('@/pages/facility/FacilitySignupPage'))
const FacilityLinkPage = lazyWithRetry(() => import('@/pages/facility/FacilityLinkPage'))
const FacilityDashboardPage = lazyWithRetry(() => import('@/pages/facility/FacilityDashboardPage'))
const FacilityStatsPage = lazyWithRetry(() => import('@/pages/facility/FacilityStatsPage'))
const FacilityProgramsPage = lazyWithRetry(() => import('@/pages/facility/FacilityProgramsPage'))
const FacilityPhotosPage = lazyWithRetry(() => import('@/pages/facility/FacilityPhotosPage'))
const FacilityPremiumPage = lazyWithRetry(() => import('@/pages/facility/FacilityPremiumPage'))
const FacilityCouponsPage = lazyWithRetry(() => import('@/pages/facility/FacilityCouponsPage'))
const FacilityReviewsPage = lazyWithRetry(() => import('@/pages/facility/FacilityReviewsPage'))
const FacilityConsultationsPage = lazyWithRetry(() => import('@/pages/facility/FacilityConsultationsPage'))
const FacilityInfoEditPage = lazyWithRetry(() => import('@/pages/facility/FacilityInfoEditPage'))
const FacilityManagerLayout = lazyWithRetry(() => import('@/components/layout/FacilityManagerLayout'))
const GoogleCallbackPage = lazyWithRetry(() => import('@/pages/GoogleCallbackPage'))
const ChangePasswordPage = lazyWithRetry(() => import('@/pages/ChangePasswordPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage'))
const KakaoCallbackPage = lazyWithRetry(() => import('@/pages/KakaoCallbackPage'))
const NaverCallbackPage = lazyWithRetry(() => import('@/pages/NaverCallbackPage'))
const NotificationsPage = lazyWithRetry(() => import('@/pages/NotificationsPage'))
const CareLogPage = lazyWithRetry(() => import('@/pages/carelog/CareLogPage'))
const CareLogSubjectNewPage = lazyWithRetry(() => import('@/pages/carelog/CareLogSubjectNewPage'))
const CareLogSubjectEditPage = lazyWithRetry(() => import('@/pages/carelog/CareLogSubjectEditPage'))
const CareLogNewPage = lazyWithRetry(() => import('@/pages/carelog/CareLogNewPage'))
const CareLogDetailPage = lazyWithRetry(() => import('@/pages/carelog/CareLogDetailPage'))
const FacilityCareLogsPage = lazyWithRetry(() => import('@/pages/facility/FacilityCareLogsPage'))
const ChecklistStepPage = lazyWithRetry(() => import('@/pages/ChecklistStepPage'))
const ChecklistResultPage = lazyWithRetry(() => import('@/pages/ChecklistResultPage'))

// =========================================================================
// 유틸리티 컴포넌트
// =========================================================================

function PageFallback() {
  return (
    <div className="flex flex-1 items-center justify-center py-20">
      <Spinner size="lg" />
    </div>
  )
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>
}

/**
 * 비공개·기능성 경로에 self-canonical + `noindex, follow` 부여.
 *
 * 영구지침: docs/develop/seo/ssr-spec.md §16.2
 *
 * `<Routes>` 의 형제로 **한 번만** 렌더한다. 페이지 내부에 두면 안 되는 이유:
 * `RequireAuth` 는 SSR 에서 `null` 을 렌더하고 `lazy` 페이지는 Suspense 폴백만 렌더해
 * 페이지 컴포넌트의 `<SEO>` 가 서버 렌더에서 실행되지 않는다(canonical 없는 200 의 원인).
 *
 * 대상이 아니면 `null` — 색인 대상 페이지(시설 상세·지역·가이드·블로그·커뮤니티)의
 * 기존 메타에는 태그를 하나도 추가하지 않는다.
 */
function NoindexRouteMeta() {
  const { pathname } = useLocation()
  const match = findNoindexRoute(pathname)
  if (!match) return null
  return <SEO title={match.title} url={match.canonicalPath} noindex />
}

/**
 * 외부 오링크 경로를 실제 경로로 301 정규화 (`/terms`, `/privacy`).
 *
 * 영구지침: docs/develop/seo/ssr-spec.md §16.4
 * SSR 은 `useSsrStatus` 로 301 + Location 을 내리고(StaticRouter 에서 `<Navigate>` 는 무동작),
 * CSR 은 `<Navigate replace>` 로 히스토리를 더럽히지 않는다. 쿼리스트링(utm 등)은 보존한다.
 */
function LegacyPathRedirect({ to }: { to: string }) {
  const ssrStatus = useSsrStatus()
  const { search } = useLocation()
  const target = `${to}${search}`
  if (ssrStatus) {
    ssrStatus.status = 301
    ssrStatus.redirect = target
    return null
  }
  return <Navigate to={target} replace />
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()
  const hydrated = useAuthHydrated()
  // persist 복원 전에는 판단 유보 (SSR HTML 과 동일하게 null 렌더 → hydrate 후 실제 판단)
  if (!hydrated) return null
  if (!isAuthenticated) {
    // 로그인 후 원래 페이지로 복귀하도록 from 전달
    const from = location.pathname + location.search
    return <Navigate to="/login" state={{ from }} replace />
  }
  // 약관 미동의(소셜 가입 직후) 사용자는 /auth/complete-signup 으로.
  // 단, /oauth2/consent/ 는 SSO 개인정보 제공 동의 플로우라 서비스 약관 체크와 무관하게
  // 진행되어야 함 (client 의 termsAgreedAt 이 stale null 인 경우 complete-signup 이
  // /me 조회 후 홈으로 튕기는 버그 방지).
  const termsExempt =
      location.pathname === '/auth/complete-signup'
      || location.pathname.startsWith('/oauth2/consent/')
  if (user && user.termsAgreedAt == null && !termsExempt) {
    return <Navigate to="/auth/complete-signup" replace />
  }
  // 프로필 미완료(전화번호 등 미입력)는 서버 가드(InquiryServiceImpl 등)에서 문의 시점에만 처리.
  // 프론트에서 전역 리다이렉트하면 기존 소셜 가입자가 마이페이지 등 모든 보호 경로에서 튕기는 이슈가 있어 제거.
  // 로그인 직후 신규 가입자 안내는 useAuth.routeAfterLogin 에서 1회만 유도.
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  const hydrated = useAuthHydrated()
  if (!hydrated) return null
  if (!isAuthenticated || (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN')) {
    return <Navigate to="/admin/login" replace />
  }
  return <>{children}</>
}

/**
 * 시설관리자 화면 가드. FACILITY 는 기존대로 통과.
 * 운영자(ADMIN·SUPER_ADMIN)는 대신 처리 대상이 저장돼 있을 때만 통과, 없으면 어드민 목록으로.
 * 영구지침: docs/develop/facility/admin-act-as.md §6.3
 */
function RequireFacility({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  const hydrated = useAuthHydrated()
  const access = decideFacilityAccess(
    { hydrated, isAuthenticated, role: user?.role },
    hydrated ? getActAsTarget() : null,
  )
  if (access.kind === 'wait') return null
  if (access.kind === 'redirect') return <Navigate to={access.to} replace />
  return <>{children}</>
}

function FacilityOrRegionResolver() {
  const { id } = useParams<{ id: string }>()
  if (/^\d+$/.test(id || '')) return <FacilityDetailPage />
  return <RegionalFacilityPage />
}

/**
 * biz.carebalance.co.kr 호스트면 자체 레이아웃의 BizIntroPage 직접 렌더,
 * 메인 호스트면 기존 Layout(Outlet → HomePage)을 사용.
 * 부모 Route element 자리에 들어가는 컴포넌트.
 */
function HostAwareHomeLayout() {
  const host = useHost()
  if (isBizHost(host)) return <BizIntroPage />
  return <Layout showHeader={false} showBottomNav />
}

// =========================================================================
// 메인 라우트 컴포넌트
// =========================================================================

export default function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    {/* 비공개·기능성 경로 self-canonical + noindex (ssr-spec §16.2) */}
    <NoindexRouteMeta />
    <Routes>
      {/* 메인 홈: 호스트 감지 분기 (biz 도메인은 자체 레이아웃의 BizIntroPage) */}
      <Route element={<HostAwareHomeLayout />}>
        <Route path="/" element={<HomePage />} />
      </Route>

      {/* 하단 탭바 레이아웃 */}
      <Route element={<Layout showHeader={false} showBottomNav />}>
        <Route path="/facilities" element={<FacilitySearchPage />} />
        <Route path="/cost-simulator" element={<CostSimulatorPage />} />
        {/* 돌봄지원 계산기 — 비로그인으로 결과까지 열람 가능 */}
        <Route path="/care-calculator" element={<CareCalculatorStartPage />} />
        <Route path="/care-calculator/profile" element={<CareCalculatorProfilePage />} />
        <Route path="/care-calculator/step" element={<CareCalculatorStepPage />} />
        <Route path="/care-calculator/result" element={<CareCalculatorResultPage />} />
        <Route path="/bookmarks" element={<RequireAuth><SuspenseWrapper><BookmarkPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage" element={<RequireAuth><SuspenseWrapper><MyPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/care-log" element={<RequireAuth><SuspenseWrapper><CareLogPage /></SuspenseWrapper></RequireAuth>} />
      </Route>

      {/* 시설 상세 + 지역별 랜딩 */}
      <Route element={<Layout showHeader={false} showBottomNav />}>
        <Route path="/facilities/:sido/:sigungu/:type" element={<RegionalFacilityPage />} />
        <Route path="/facilities/:sido/:sigungu" element={<RegionalFacilityPage />} />
        <Route path="/facilities/:id" element={<FacilityOrRegionResolver />} />
      </Route>

      {/* 헤더 없는 레이아웃 */}
      <Route element={<Layout showHeader={false} showBottomNav={false} />}>
        <Route path="/recommend" element={<RequireAuth><SuspenseWrapper><RecommendInputPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/recommend/result" element={<RequireAuth><SuspenseWrapper><RecommendResultPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/mypage/edit" element={<RequireAuth><SuspenseWrapper><MyPageEdit /></SuspenseWrapper></RequireAuth>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/guide/care-grade" element={<GuideCareGradePage />} />
        <Route path="/guide/facility-types" element={<GuideFacilityTypePage />} />
        <Route path="/guide/cost" element={<GuideCostPage />} />
        <Route path="/guide/resources" element={<GuideResourcesPage />} />
        <Route path="/guide/self-assessment" element={<SelfAssessmentPage />} />
        <Route path="/guide/self-assessment/result" element={<SelfAssessmentResultPage />} />
        <Route path="/checklist" element={<ChecklistIntroPage />} />
        <Route path="/checklist/step/:n" element={<SuspenseWrapper><ChecklistStepPage /></SuspenseWrapper>} />
        <Route path="/checklist/result" element={<SuspenseWrapper><ChecklistResultPage /></SuspenseWrapper>} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/community" element={<SuspenseWrapper><CommunityHomePage /></SuspenseWrapper>} />
        <Route path="/community/:slug" element={<SuspenseWrapper><BoardPage /></SuspenseWrapper>} />
        <Route path="/community/:slug/write" element={<RequireAuth><SuspenseWrapper><PostWritePage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/community/posts/:id" element={<SuspenseWrapper><PostDetailPage /></SuspenseWrapper>} />
        <Route path="/community/posts/:id/edit" element={<RequireAuth><SuspenseWrapper><PostWritePage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/change-password" element={<RequireAuth><SuspenseWrapper><ChangePasswordPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/parent-info" element={<RequireAuth><SuspenseWrapper><ParentInfoPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/consultations" element={<RequireAuth><SuspenseWrapper><MyConsultationsPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/inquiry" element={<RequireAuth><SuspenseWrapper><InquiryListPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/inquiry/new" element={<RequireAuth><SuspenseWrapper><InquiryNewPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/inquiry/:id" element={<RequireAuth><SuspenseWrapper><InquiryDetailPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/notifications" element={<RequireAuth><SuspenseWrapper><NotificationsPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/notification-settings" element={<RequireAuth><SuspenseWrapper><NotificationSettingsPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/care-log/subject/new" element={<RequireAuth><SuspenseWrapper><CareLogSubjectNewPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/care-log/subject/:subjectId/edit" element={<RequireAuth><SuspenseWrapper><CareLogSubjectEditPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/care-log/new" element={<RequireAuth><SuspenseWrapper><CareLogNewPage /></SuspenseWrapper></RequireAuth>} />
        <Route path="/mypage/care-log/:id" element={<RequireAuth><SuspenseWrapper><CareLogDetailPage /></SuspenseWrapper></RequireAuth>} />
      </Route>

      {/* 관리자 */}
      <Route path="/admin/login" element={<SuspenseWrapper><AdminLoginPage /></SuspenseWrapper>} />
      <Route path="/admin" element={<RequireAdmin><SuspenseWrapper><AdminLayout /></SuspenseWrapper></RequireAdmin>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<SuspenseWrapper><AdminDashboardPage /></SuspenseWrapper>} />
        <Route path="consultations" element={<SuspenseWrapper><AdminConsultationsPage /></SuspenseWrapper>} />
        <Route path="facility-search" element={<SuspenseWrapper><AdminFacilitySearchPage /></SuspenseWrapper>} />
        <Route path="facility-search/:id" element={<SuspenseWrapper><AdminFacilityDetailPage /></SuspenseWrapper>} />
        <Route path="facility-managers" element={<SuspenseWrapper><AdminFacilityManagersPage /></SuspenseWrapper>} />
        <Route path="facility-managers/:id" element={<SuspenseWrapper><AdminFacilityManagerDetailPage /></SuspenseWrapper>} />
        <Route path="inquiries" element={<SuspenseWrapper><AdminInquiriesPage /></SuspenseWrapper>} />
        <Route path="promotions" element={<SuspenseWrapper><AdminPromotionsPage /></SuspenseWrapper>} />
        <Route path="blog" element={<SuspenseWrapper><AdminBlogListPage /></SuspenseWrapper>} />
        <Route path="blog/new" element={<SuspenseWrapper><AdminBlogEditorPage /></SuspenseWrapper>} />
        <Route path="blog/:id/edit" element={<SuspenseWrapper><AdminBlogEditorPage /></SuspenseWrapper>} />
        <Route path="push" element={<SuspenseWrapper><AdminPushSendPage /></SuspenseWrapper>} />
        <Route path="popups" element={<SuspenseWrapper><AdminPopupListPage /></SuspenseWrapper>} />
        <Route path="popups/new" element={<SuspenseWrapper><AdminPopupEditorPage /></SuspenseWrapper>} />
        <Route path="popups/:id/edit" element={<SuspenseWrapper><AdminPopupEditorPage /></SuspenseWrapper>} />
        <Route path="subscriptions" element={<SuspenseWrapper><AdminSubscriptionsPage /></SuspenseWrapper>} />
        <Route path="facility-photos" element={<SuspenseWrapper><AdminFacilityPhotosPage /></SuspenseWrapper>} />
        <Route path="reviews" element={<SuspenseWrapper><AdminReviewsPage /></SuspenseWrapper>} />
        <Route path="users" element={<SuspenseWrapper><AdminUsersPage /></SuspenseWrapper>} />
        <Route path="users/:id" element={<SuspenseWrapper><AdminUserDetailPage /></SuspenseWrapper>} />
        <Route path="error-logs" element={<SuspenseWrapper><AdminErrorLogsPage /></SuspenseWrapper>} />
        <Route path="notifications" element={<SuspenseWrapper><NotificationsPage /></SuspenseWrapper>} />
        <Route path="email" element={<SuspenseWrapper><AdminEmailPage /></SuspenseWrapper>} />
        <Route path="newsletter" element={<SuspenseWrapper><AdminNewsletterListPage /></SuspenseWrapper>} />
        <Route path="newsletter/new" element={<SuspenseWrapper><AdminNewsletterEditorPage /></SuspenseWrapper>} />
        <Route path="newsletter/:id" element={<SuspenseWrapper><AdminNewsletterEditorPage /></SuspenseWrapper>} />
        <Route path="coupons" element={<SuspenseWrapper><AdminCouponListPage /></SuspenseWrapper>} />
        <Route path="coupons/new" element={<SuspenseWrapper><AdminCouponCreatePage /></SuspenseWrapper>} />
        <Route path="coupons/:id" element={<SuspenseWrapper><AdminCouponDetailPage /></SuspenseWrapper>} />
        <Route path="card-news" element={<SuspenseWrapper><AdminCardNewsPage /></SuspenseWrapper>} />
        <Route path="facility-data" element={<SuspenseWrapper><AdminFacilityDataPage /></SuspenseWrapper>} />
        <Route path="community/boards" element={<SuspenseWrapper><AdminBoardsPage /></SuspenseWrapper>} />
        <Route path="community/boards/new" element={<SuspenseWrapper><AdminBoardEditPage /></SuspenseWrapper>} />
        <Route path="community/boards/:id/edit" element={<SuspenseWrapper><AdminBoardEditPage /></SuspenseWrapper>} />
        <Route path="oauth-clients" element={<SuspenseWrapper><AdminOAuthClientsPage /></SuspenseWrapper>} />
      </Route>

      {/* OAuth2 Cafe24 Bridge — /oauth2/cafe24/authorize 진입 후 SPA 에서 인증 상태 확인 + consent code 발급 */}
      <Route path="/oauth2/bridge" element={<SuspenseWrapper><BridgePage /></SuspenseWrapper>} />

      {/* 쇼핑몰 단축 링크 — 인증 상태에 따라 SSO 자동 로그인 또는 직접 이동. 어드민 배너/메뉴 공용 */}
      <Route path="/shop" element={<SuspenseWrapper><ShopRedirectPage /></SuspenseWrapper>} />

      {/* OAuth2 동의 화면 (로그인 필요) */}
      <Route path="/oauth2/consent/:codeId" element={<RequireAuth><SuspenseWrapper><ConsentPage /></SuspenseWrapper></RequireAuth>} />

      {/* 시설관리자 */}
      <Route path="/facility/login" element={<SuspenseWrapper><FacilityLoginPage /></SuspenseWrapper>} />
      <Route path="/facility/signup" element={<SuspenseWrapper><FacilitySignupPage /></SuspenseWrapper>} />
      <Route path="/facility" element={<RequireFacility><SuspenseWrapper><FacilityManagerLayout /></SuspenseWrapper></RequireFacility>}>
        <Route index element={<Navigate to="/facility/dashboard" replace />} />
        <Route path="link" element={<SuspenseWrapper><FacilityLinkPage /></SuspenseWrapper>} />
        <Route path="dashboard" element={<SuspenseWrapper><FacilityDashboardPage /></SuspenseWrapper>} />
        <Route path="stats" element={<SuspenseWrapper><FacilityStatsPage /></SuspenseWrapper>} />
        <Route path="consultations" element={<SuspenseWrapper><FacilityConsultationsPage /></SuspenseWrapper>} />
        <Route path="reviews" element={<SuspenseWrapper><FacilityReviewsPage /></SuspenseWrapper>} />
        <Route path="care-logs" element={<SuspenseWrapper><FacilityCareLogsPage /></SuspenseWrapper>} />
        <Route path="notifications" element={<SuspenseWrapper><NotificationsPage /></SuspenseWrapper>} />
        <Route path="info" element={<SuspenseWrapper><FacilityInfoEditPage /></SuspenseWrapper>} />
        <Route path="programs" element={<SuspenseWrapper><FacilityProgramsPage /></SuspenseWrapper>} />
        <Route path="photos" element={<SuspenseWrapper><FacilityPhotosPage /></SuspenseWrapper>} />
        <Route path="inquiry" element={<SuspenseWrapper><InquiryListPage /></SuspenseWrapper>} />
        <Route path="inquiry/new" element={<SuspenseWrapper><InquiryNewPage /></SuspenseWrapper>} />
        <Route path="inquiry/:id" element={<SuspenseWrapper><InquiryDetailPage /></SuspenseWrapper>} />
        <Route path="premium" element={<SuspenseWrapper><FacilityPremiumPage /></SuspenseWrapper>} />
        <Route path="coupons" element={<SuspenseWrapper><FacilityCouponsPage /></SuspenseWrapper>} />
      </Route>

      {/* 약관 */}
      <Route path="/terms/:type" element={<TermsPage />} />
      {/* 외부 오링크 301 — 실제 약관 경로로 정규화 (ssr-spec §16.4). 404 로 버리지 않는다 */}
      <Route path="/terms" element={<LegacyPathRedirect to="/terms/terms-of-service" />} />
      <Route path="/privacy" element={<LegacyPathRedirect to="/terms/privacy-policy" />} />

      {/* 이메일 수신거부 (비로그인 공개) */}
      <Route path="/unsubscribe" element={<SuspenseWrapper><UnsubscribePage /></SuspenseWrapper>} />

      {/* 약관 동의 완료 (소셜 가입 직후) */}
      <Route path="/auth/complete-signup" element={<RequireAuth><SuspenseWrapper><CompleteSignupPage /></SuspenseWrapper></RequireAuth>} />

      {/* 정보 확인 (소셜 가입 — 동의 후) */}
      <Route path="/auth/complete-profile" element={<RequireAuth><SuspenseWrapper><CompleteProfilePage /></SuspenseWrapper></RequireAuth>} />

      {/* 인증 */}
      <Route path="/login" element={<SuspenseWrapper><LoginPage /></SuspenseWrapper>} />
      <Route path="/signup" element={<SuspenseWrapper><SignupPage /></SuspenseWrapper>} />
      <Route path="/forgot-password" element={<SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>} />
      <Route path="/reset-password" element={<SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>} />
      <Route path="/auth/kakao/callback" element={<SuspenseWrapper><KakaoCallbackPage /></SuspenseWrapper>} />
      <Route path="/auth/naver/callback" element={<SuspenseWrapper><NaverCallbackPage /></SuspenseWrapper>} />
      <Route path="/auth/google/callback" element={<SuspenseWrapper><GoogleCallbackPage /></SuspenseWrapper>} />

      {/* 404 — soft-404 방지: 홈 리다이렉트 대신 명시적 404 페이지 (SSR 시 HTTP 404) */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  )
}
